import { Links, Meta, Outlet, Scripts, useRouteLoaderData, type LoaderFunctionArgs } from 'react-router';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import 'assets/styles/index.scss';
import '@entur/a11y/dist/styles.css';
import { UniformenHeadAssets, UniformenHeader, UniformenScripts } from 'components/Uniformen';
import { getUniformen } from './server/uniformen.server';

dayjs.extend(customParseFormat);
dayjs.extend(duration);

// Apply the saved color mode before first paint, kept in sync by UserSettingsProvider
const colorModeScript = `(function () {
  var mode;
  try {
    mode = JSON.parse(localStorage.getItem('CLEOSCLIENT_user_settings') || '{}').colorMode;
  } catch (e) {}
  if (mode === 'system') {
    mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } else if (mode !== 'dark') {
    mode = 'light';
  }
  document.documentElement.dataset.colorMode = mode;
})();`;

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // The header is optional: whatever goes wrong while getting it, the page renders without it
  try {
    return { uniformen: await getUniformen(request) };
  } catch (error) {
    console.error('Failed to get the Uniformen header', error);
    return { uniformen: null };
  }
};

// The header is fetched for the document only. Client-side navigation doesn't change it, and
// login and logout are full page loads.
export const shouldRevalidate = () => false;

export const Layout = ({ children }: { children: React.ReactNode }) => {
  // undefined when an error is rendered before the loader ran
  const uniformen = useRouteLoaderData<typeof loader>('root')?.uniformen ?? null;

  return (
    // colorModeScript sets data-color-mode, and Uniformen's head script data-uniformen-sidebar, on
    // <html> before hydration. data-uniformen tells the CSS to hide CLEOS's own branding.
    <html lang="nb-NO" data-uniformen={uniformen ? '' : undefined} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>CLEOS</title>
        {/* Served by server/ in production; the dev server uses .env.development instead */}
        {import.meta.env.PROD && <script src="/appConfig.js" />}
        <script dangerouslySetInnerHTML={{ __html: colorModeScript }} />
        <UniformenHeadAssets uniformen={uniformen} />
        <Meta />
        <Links />
      </head>
      <body>
        <UniformenHeader uniformen={uniformen} />
        {children}
        <UniformenScripts uniformen={uniformen} />
        <Scripts />
      </body>
    </html>
  );
};

const Root = () => <Outlet />;

export default Root;
